// A studio's tags: the record, what makes two of them the same, and the one-off
// that turns the legacy strings on students into records.
//
// ── What a tag is (#138) ───────────────────────────────────────────────────
//
// In MyMusicStaff's `Students → Tags` tab a tag is a **row in a managed
// collection**, and the record carries:
//
//   Name          required
//   Tag Type      a dropdown on the Edit drawer (`Subject` for an instrument
//                 tag): one record with a type on it, not two collections
//   Color         a swatch dropdown, defaulting to `Beach`, from the same
//                 palette the category picker uses
//   Students      a multi-select with Quick Select, Clear Selection and a
//                 "Show inactive students in this list" checkbox — so
//                 **membership is edited from the tag**
//   Description   free text, optional
//
// The grid shows Name (with the kind as a badge under it), Color, and Students
// **by name rather than as a count**. The row menu is Edit and Delete.
// Deleting a tag that still has members is **not gated**: the confirm reads
// "Are you sure you want to delete Piano from your tags?", with no count and
// no offer to reassign. This file mirrors all of that.
//
// A list of spellings derived from `instrumentTags` / `groupTags` — the legacy
// free-text fields on a student — would have no row to hang a colour or a
// description on and no membership to edit.
//
// `tagVocabulary` and the tie-refusing `canonicalSpelling` are the
// **migration's** internals: they decide what to *name* the record a
// student's strings become.
//
// **Membership lives on the tag, so a rename is one write.** Categories differ:
// `categoryId` sits on each event and each student beside a denormalised
// label, so `relabelCategory` fans out over two collections (data/useEvents.jsx).
// A tag holds its own `studentIds`, so renaming it touches one document.
//
// Pure, like the rest of domain/ — the migration script drives the same
// functions the app does (#110).

import { categoryKey, SWATCHES } from './categories.js';
import { freeId } from './freeId.js';
// `./core.js`, not `../lib/core.js`: this file is copied verbatim into
// functions/domain/, and that door is inside the copy (#218).
import { coreSync } from './core.js';
import { documentFromRows } from './documents.js';

// The two kinds, as MMS stores them: one field on one record.
//
// MMS's Add flow has two entry points — "Group Tag" and "Instrument Tags" —
// and its Edit drawer carries a `Tag Type` dropdown the Add form does not:
// one record, an editable type.
//
// MMS's word is `Subject`; ours is `instrument`, the word on every other
// surface here (`instrumentTags` on the student, "Instrument Tags" on the
// wizard and on the students list's column).
export const TAG_KINDS = Object.freeze({ instrument: 'instrument', group: 'group' });

export const TAG_KIND_IDS = Object.freeze([TAG_KINDS.instrument, TAG_KINDS.group]);

export const TAG_KIND_LABELS = Object.freeze({
  [TAG_KINDS.instrument]: 'Instrument',
  [TAG_KINDS.group]: 'Group',
});

// MMS's own copy from the Add Tag chooser, the only place either kind is
// explained, kept verbatim in substance. See the note on `tagKind` below for
// what the difference does here.
export const TAG_KIND_BLURBS = Object.freeze({
  [TAG_KINDS.instrument]:
    'Same as a group tag, but also appears in the booking form so students can '
    + 'select an instrument when scheduling.',
  [TAG_KINDS.group]:
    'Use when you want to group students internally for easier organization '
    + 'and filtering.',
});

// The kind a tag is when nothing says otherwise.
//
// `instrument` rather than `group`, because the wizard puts "Instrument Tags"
// first.
export const DEFAULT_TAG_KIND = TAG_KINDS.instrument;

// An MMS instrument tag appears in the *online booking form*, where a student
// picks an instrument when scheduling. The family portal's booking
// (functions/portal/book.js) reads no tag, so here the kind is a badge, a
// filter and a column heading; it is stored and editable on the record, as in
// MMS.
//
// `tags::tag_kind` is the core's, with the three rules below it —
// `clashingTag`, `duplicateTagMessage` and `membershipDelta` — so the phones'
// tag pickers refuse and write what this one does.
// `core/src/people_forms_vectors.json` holds their recorded answers.
export function tagKind(tag) {
  const kind = tag?.kind;
  return coreSync().tagKind(typeof kind === 'string' ? kind : null);
}

// When two tags are the same tag.
//
// `categoryKey`, not a second normaliser: like `resolveImportCategory` in
// domain/importDelimited.js, this asks *is this string the name of a thing you
// already have?*, and two answers that disagree would fold one way here and
// another elsewhere.
//
// So "Piano" and "piano" are one tag (case), so are `" Piano "` and `"Piano"`
// (surrounding whitespace) and `"Group  Class"` and `"Group Class"` (internal
// runs), and so are a composed "é" and a decomposed one (NFC). "Lessson" is
// still a different word.
//
// The key is shared by an editor that *refuses* a duplicate name (`tagErrors`
// below) and a migration that *matches* one (`planTagMigration` at the foot of
// the file); loosening one side alone admits rows the other can never repair.
export function tagKey(label) {
  return categoryKey(label);
}

// The tags as the core's rows: a name, a kind, and each tag's members beside
// it keyed by position.
const tagRow = (t) => ({
  label: typeof t?.label === 'string' ? t.label : (t?.label == null ? null : String(t.label)),
  kind: typeof t?.kind === 'string' ? t.kind : null,
});
const memberRows = (tags) => tags.flatMap((t, tag) => (Array.isArray(t?.studentIds) ? t.studentIds : [])
  .map((id) => ({ tag, studentId: typeof id === 'string' ? id : null })));

// The studio's tags in the order the tab shows them: by name, as the browser
// compares names, a tag with none first — `sort_tags` in core/src/tags.rs. The
// kind is not a grouping key; MMS shows one grid with the kind as a badge under
// the name.
export function sortTags(tags = []) {
  const list = [...tags];
  return [...coreSync().sortTags(list.map((t) => tagRow(t).label || ''))].map((i) => list[i]);
}

// The tag this draft's name collides with, or null.
//
// `selfId` is the draft's own id when one is being edited, so renaming a tag to
// the name it already has is not a collision with itself.
//
// **Scoped to the kind.** Whether MMS refuses a duplicate tag name is not
// known. This refuses within a kind and allows across, so a studio can have an
// instrument tag "Piano" and a group tag "Piano": two rows with different
// badges are distinguishable on screen, two with the same badge are not.
//
// The kind is editable, so switching a group "Piano" to an instrument tag
// beside an instrument "Piano" is a same-kind duplicate made without typing a
// name. The draft's kind is what is checked, not the stored one, so that route
// is refused too.
//
// The rule is `tags::clashing_tag`'s, which answers with a position so the
// caller gets its own tag object back. A tag is "itself" only when both ids
// are strings and equal, so a tag whose id is `null` is not excluded when
// `selfId` is — unreachable, as both callers pass `?? null` and every stored
// tag has an id.
export function clashingTag(label, kind, selfId, tags = []) {
  const str = (v) => (typeof v === 'string' ? v : null);
  const refs = tags.map((t) => ({ id: str(t?.id), kind: str(t?.kind), label: str(t?.label) }));
  const at = coreSync().clashingTag(str(label), str(kind), str(selfId), refs);
  return at === undefined || at === null ? null : tags[at];
}

// Our wording, MMS's shape. It names the *existing* tag's own spelling, so a
// clash of case or spacing reads right: a teacher typing "piano" is told there
// is already a "Piano". Same construction as `duplicateLabelMessage`.
export function duplicateTagMessage(existingLabel) {
  return coreSync().duplicateTagMessage(String(existingLabel));
}

// What Add Tag and Edit Tag refuse, keyed by the field it belongs under. The
// shape `categoryErrors` has, exported pure so the rule can be exercised
// without a drawer.
//
// The refusal arrives **on submit**, not as a greyed button — #116's shape,
// which is how MMS refuses an expense. The empty name still greys the button,
// as on the category drawer.
//
// Uniqueness is enforced here rather than in `firestore.rules` or a
// deterministic id, for the reasons at `categoryErrors` in
// domain/categories.js. Two offline devices both adding "Piano" still converge
// on two documents, because neither could see the other's write.
export function tagErrors(draft, tags = []) {
  const errors = {};
  const clash = clashingTag(draft?.label, draft?.kind, draft?.id ?? null, tags);
  if (clash) errors.label = duplicateTagMessage(clash.label);
  return errors;
}

// The members of a tag, as a Set, tolerating a document written without the
// field. Firestore has no empty-array/absent distinction worth honouring here.
export function tagMembers(tag) {
  const ids = Array.isArray(tag?.studentIds) ? tag.studentIds : [];
  return new Set(coreSync().tagMembers(ids.map((id) => (typeof id === 'string' ? id : ''))));
}

// What a save changes about a tag's membership: who joined, and who left.
//
// The unit `useTags.saveTag` writes (#198, #288). The drawer hands back the
// membership it wants; this turns that into the two *operations* that reach it
// from the one this device last saw, so the write can be an `arrayUnion` of the
// joiners and an `arrayRemove` of the leavers rather than a whole array. Two
// devices editing one tag offline then commute at Firestore instead of one
// overwriting the other.
//
// **`before` is a tag document or null**, not a list: a create passes null, an
// edit passes the row from the live snapshot. A null `before` means "this
// document has no membership yet", and the delta is all joiners and no leavers
// — the safe direction, since an `arrayRemove` is the only half that can take
// something away.
//
// Both sides are deduplicated and stripped of falsy entries through
// `tagMembers`, so `added` and `removed` are disjoint and neither can contain a
// value `firestore.rules` would refuse. Order is the caller's, which keeps the
// write diffable against what the drawer showed.
export function membershipDelta(before, studentIds) {
  // Strings only across the boundary; the core drops the empty ones and the
  // repeats, as `tagMembers` does.
  const ids = (list) => (list || []).filter((id) => typeof id === 'string');
  return coreSync().membershipDelta(ids(before?.studentIds), ids(studentIds));
}

// The two writes `saveTag` in data/useTags.jsx makes to one tag (the phones'
// tag drawers write the same pair): the tag's own fields with an `arrayUnion`
// of the students joining, and — only when somebody left — a second merge with
// an `arrayRemove` of them. The reasons are at `saveTag`. `added` and `removed` are `membershipDelta`'s;
// `arrayUnion`, `arrayRemove` and `serverTimestamp` are the writer's sentinels.
//
// `tags::tag_document` and `tags::tag_removal_document` in the core.
export function tagWrites({ label, kind, color, description } = {}, { added = [], removed = [] } = {}, {
  serverTimestamp, arrayUnion, arrayRemove,
} = {}) {
  const text = (v) => (v ? String(v) : null);
  const fields = documentFromRows(coreSync().tagDocument(
    text(label), text(kind), text(color), text(description), (added || []).map(String),
  ), serverTimestamp, { arrayUnion });
  const rows = coreSync().tagRemovalDocument((removed || []).map(String));
  return {
    fields,
    removal: rows.length ? documentFromRows(rows, serverTimestamp, { arrayRemove }) : null,
  };
}

// The tags one student carries, in tab order.
//
// Ask the tags who is in them, rather than asking the students what they are
// called.
export function tagsForStudent(tags = [], studentId, kind = null) {
  if (!studentId) return [];
  return [...coreSync().tagsForStudent(tags.map(tagRow), memberRows(tags), String(studentId), kind == null ? null : String(kind))]
    .map((i) => tags[i]);
}

// Every student's tags at once, keyed by student id.
//
// The list does this per row and per column, so it is one pass over the tags,
// the small collection, rather than a scan per student.
export function tagsByStudent(tags = []) {
  const byStudent = new Map();
  for (const { studentId, tag } of coreSync().tagsByStudent(tags.map(tagRow), memberRows(tags))) {
    if (!byStudent.has(studentId)) byStudent.set(studentId, []);
    byStudent.get(studentId).push(tags[tag]);
  }
  return byStudent;
}

// The field on a student that holds a kind's legacy strings.
//
// Legacy, and **nothing writes them** (#138, #328): the Add Student wizard and
// `EditStudentDetailsDrawer` both carry a picker over this collection, so a
// student's tags are memberships. Students written before the collection
// existed may still carry these strings; `scripts/migrate-tags.mjs` turns them
// into records and leaves them in place, so it can run at any time and twice.
const LEGACY_FIELD = Object.freeze({
  [TAG_KINDS.instrument]: 'instrumentTags',
  [TAG_KINDS.group]: 'groupTags',
});

// Every tag name a student is showing under, from both sources, folded.
//
// **Two sources.** The managed records are the answer; the legacy strings are
// what a student written before the collection existed carries, and showing
// only one would hide the other's tags (#138, #328). So: the union, folded on
// `tagKey`, so that a student in the "Piano" record who also carries the string
// "piano" reads as one tag. The managed spelling wins, because a record is the
// thing a teacher can rename.
//
// This is the one place the union is taken. No surface writes a legacy string,
// so the set of students carrying one only shrinks, and a
// `scripts/migrate-tags.mjs` run empties it.
export function studentTagLabels(student, tags = [], kind = TAG_KINDS.instrument) {
  const legacy = (student?.[LEGACY_FIELD[kind]] || []).map((raw) => (typeof raw === 'string' ? raw : String(raw)));
  return coreSync().studentTagLabels(
    tags.map(tagRow), memberRows(tags), student?.id ? String(student.id) : null, legacy, String(kind),
  );
}

// ─── Turning the strings a studio already has into records ─────────────────
//
// Everything below exists for a **one-off** run, driven by
// scripts/migrate-tags.mjs, and must never become a read path.
//
// It reads a student's strings, decides what **record** they describe, and
// leaves the strings alone — nothing a student carries is edited.

// The studio's tags as its students currently spell them, grouped by key.
//
// Nothing reads it except the migration below, and it is exported for the
// tests that pin the grouping rather than for a caller.
//
// Each entry:
//
//   key         what the spellings have in common
//   spellings   every spelling, `{ label, count }`, most-used first
//   studentIds  the students carrying the tag under **any** spelling, in roster
//               order, deduplicated
//
// A student with no id cannot be a member of anything, so it contributes its
// spellings and not its membership (only a fixture has no id).
export function tagVocabulary(students = []) {
  const build = (field) => {
    const byKey = new Map();
    for (const s of students || []) {
      const seenKeys = new Set();
      for (const raw of s?.[field] || []) {
        const label = typeof raw === 'string' ? raw : String(raw ?? '');
        const key = tagKey(label);
        if (!key) continue;
        if (!byKey.has(key)) byKey.set(key, { key, studentIds: [], spellings: new Map() });
        const entry = byKey.get(key);
        if (!seenKeys.has(key)) {
          seenKeys.add(key);
          if (s?.id) entry.studentIds.push(s.id);
        }
        const spelt = label.trim();
        entry.spellings.set(spelt, (entry.spellings.get(spelt) || 0) + 1);
      }
    }
    return [...byKey.values()]
      .map((entry) => ({
        key: entry.key,
        studentIds: entry.studentIds,
        spellings: [...entry.spellings.entries()]
          .map(([label, count]) => ({ label, count }))
          .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label)),
      }))
      .sort((a, b) => b.studentIds.length - a.studentIds.length
        || a.spellings[0].label.localeCompare(b.spellings[0].label));
  };

  return {
    [TAG_KINDS.instrument]: build('instrumentTags'),
    [TAG_KINDS.group]: build('groupTags'),
  };
}

// What to call the record these spellings become, or null if the studio has not
// made that decision for itself.
//
// The most-used spelling wins. A **tie refuses** (#110's `ambiguous` verdict):
// "Piano" on three students and "piano" on three others has no non-arbitrary
// winner, and the migration runs unattended, so it names nothing. A key with
// one spelling is not a tie.
export function canonicalSpelling(entry) {
  const spellings = entry?.spellings || [];
  if (spellings.length === 0) return null;
  if (spellings.length === 1) return spellings[0].label;
  return spellings[0].count === spellings[1].count ? null : spellings[0].label;
}

// What the migration would do, and why.
//
// Three outcomes, and the shape is `planCategoryBackfill`'s:
//
//   create  no record answers to this key in this kind. One is minted, named
//           after the settled spelling, with every student carrying it as its
//           members.
//   join    a record already answers to it — a second run, or a tag the teacher
//           made by hand. The students it is missing are added; **nothing else
//           about the record is touched**, not its name, colour or description.
//   tie     the spellings tie and no record exists to inherit a name from.
//           Nothing is created and nobody is joined. Reported once per key
//           rather than once per student.
//
// Idempotent: run twice and the second pass finds every key joined and every
// member present, and proposes nothing.
//
// Colours are dealt round the palette rather than defaulted, so migrated tags
// are not a column of identical chips. Deterministic: the nth tag created gets
// the nth swatch.
//
// Ids come from `newId` so the plan is reproducible under test; the script
// passes nothing and gets `freeId`.
export function planTagMigration({ students = [], tags = [], newId = null } = {}) {
  const mint = newId || (() => freeId('tag'));
  const vocabulary = tagVocabulary(students);

  const existing = new Map();
  for (const t of tags) {
    const key = tagKey(t?.label);
    if (!key || !t?.id) continue;
    const at = `${tagKind(t)}:${key}`;
    if (!existing.has(at)) existing.set(at, t);
  }

  const creates = [];
  const joins = [];
  const ties = [];

  for (const kind of TAG_KIND_IDS) {
    for (const entry of vocabulary[kind]) {
      const found = existing.get(`${kind}:${entry.key}`);
      if (found) {
        const members = tagMembers(found);
        const add = entry.studentIds.filter((id) => !members.has(id));
        if (add.length) {
          joins.push({ id: found.id, label: found.label, kind, addStudentIds: add });
        }
        continue;
      }
      const label = canonicalSpelling(entry);
      if (label == null) {
        ties.push({ key: entry.key, kind, spellings: entry.spellings });
        continue;
      }
      creates.push({
        id: mint(),
        label,
        kind,
        color: SWATCHES[creates.length % SWATCHES.length],
        description: '',
        studentIds: entry.studentIds,
      });
    }
  }

  return {
    creates,
    joins,
    ties,
    counts: {
      students: (students || []).length,
      creates: creates.length,
      joins: joins.length,
      members: creates.reduce((n, c) => n + c.studentIds.length, 0)
        + joins.reduce((n, j) => n + j.addStudentIds.length, 0),
      ties: ties.length,
    },
  };
}

// What a student delete merges onto a tag that still names somebody it
// removed (`planTagDetachment`, #320): those students leaving by
// `arrayRemove`, and the time the tag changed. Shared by `deleteStudents` in
// src/data/useStudents.jsx and the phones.
export function tagDetachmentDocument(studentIds = [], { serverTimestamp, arrayRemove } = {}) {
  return documentFromRows(
    coreSync().tagDetachmentDocument(studentIds.map(String)), serverTimestamp, { arrayRemove },
  );
}

// ─── The Tags tab — `core/src/tags.rs`'s ───────────────────────────────────

const tagDraft = (d, extra = {}) => ({
  ...extra,
  label: d.label,
  kind: d.kind,
  color: d.color,
  description: d.description,
  studentIds: extra.studentIds || [],
  showInactive: false,
});

// The form's draft for a new tag of a kind, and for a stored one — its kind
// through `tagKind`, so a record written before the field existed opens on a
// kind the dropdown can show.
export const emptyTagDraft = (kind) => tagDraft(coreSync().emptyTagDraft(kind ? String(kind) : null));

export function tagDraftOf(tag) {
  const d = coreSync().tagDraftOf(
    tag.label ? String(tag.label) : null, typeof tag.kind === 'string' ? tag.kind : null,
    tag.color ? String(tag.color) : null, tag.description ? String(tag.description) : null,
  );
  const { label, kind, color, description } = d;
  return { id: tag.id, ...tagDraft({ label, kind, color, description }, { studentIds: [...tagMembers(tag)] }) };
}

// "3 tags", "1 tag".
export const tagCountLabel = (n) => coreSync().tagCountLabel(Number(n) || 0);

// A row's Students cell: its members that still exist, by name, sorted.
export function tagMemberNames(tag, students = [], nameOf = (st) => st?.id) {
  const ids = (Array.isArray(tag?.studentIds) ? tag.studentIds : []).map((id) => (typeof id === 'string' ? id : ''));
  return coreSync().tagMemberNames(ids, students.map((st) => ({ id: String(st.id), name: String(nameOf(st)) })));
}

// MMS's confirm.
export const tagDeleteMessage = (label) => coreSync().tagDeleteMessage(String(label));

// "Edit Tag", or "Add Instrument Tag".
export const tagFormTitle = (kind, editing) => coreSync().tagFormTitle(kind ? String(kind) : null, Boolean(editing));

// Save is offered once the name is more than white space.
export const tagCanSave = (label) => coreSync().tagCanSave(label == null ? null : String(label));

// The Students picker: everybody but the inactive, unless the box says to
// show them — never hiding a member — and whether each row says "Inactive".
export function tagPickerRows(students = [], { selected = [], showInactive = false } = {}) {
  return coreSync().tagPickerRows(
    students.map((st) => ({ id: String(st.id), status: st.status == null ? null : String(st.status) })),
    [...selected].map(String), Boolean(showInactive),
  ).map((r) => ({ student: students[r.student], inactive: r.inactive }));
}

export const tagSelectedLabel = (n) => coreSync().tagSelectedLabel(Number(n) || 0);

// The Tags tab's words.
export const TAGS_WORDING = Object.freeze({
  addTag: '+ Add Tag',
  chooserTitle: 'Add Tag',
  groupTag: 'Group Tag',
  instrumentTag: 'Instrument Tag',
  emptyTitle: "You don't have any tags yet",
  emptyDescription: 'Keep your students organized by assigning them tags.',
  columnName: 'Name',
  columnColor: 'Color',
  columnStudents: 'Students',
  noStudents: 'No students',
  edit: 'Edit',
  delete: 'Delete',
  deleteTitle: 'Delete Tag',
  cancel: 'Cancel',
  save: 'Save',
  fieldName: 'Name',
  fieldKind: 'Tag Type',
  fieldKindHint: 'Instrument tags will appear in the online booking form when there is one.',
  fieldColor: 'Color',
  fieldStudents: 'Students',
  clearSelection: 'Clear Selection',
  nobodyToShow: 'No students to show.',
  showInactive: 'Show inactive students in this list',
  fieldDescription: 'Description',
  inactiveMark: 'Inactive',
});

// The chooser's note, which names the product.
export const tagChooserNote = (brandName) => coreSync().tagChooserNote(String(brandName));
